import CustomButton from 'Components/CustomButton'
import CustomDatePicker from 'Components/CustomDatePicker'
import StyledAccordion from 'Components/StyledAccordion'
import type { AffiliateOrderParamShape } from 'Services/SalesManagement/Features/Order/orderSlice'
import type { AffiliateOrderFilterChoices } from 'Services/SalesManagement/Features/Order/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { FC } from 'react'
import { t } from 'ttag'

const AffiliateOrderFilters: FC<{
  isFiltersLoading: boolean
  filterChoices: AffiliateOrderFilterChoices | undefined
  currentFilters: Partial<AffiliateOrderParamShape>
  clearFilters: () => void
  submitFilters: () => void
  handleDatePickerChange: (value: Date | null, name: string) => void
  handleSelectChange: (event: SelectChangeEvent, name: keyof AffiliateOrderParamShape) => void
}> = ({
  isFiltersLoading,
  filterChoices,
  currentFilters,
  clearFilters,
  submitFilters,
  handleDatePickerChange,
  handleSelectChange
}) => {
  return (
    <StyledAccordion sx={{ mb: 3 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <FilterListIcon sx={{ mr: 1 }} color="primary" />
        <Typography component="span">{t`Filters`}</Typography>
      </AccordionSummary>
      <Divider />
      {isFiltersLoading ? (
        <Box>
          <LinearProgress />
        </Box>
      ) : (
        <AccordionDetails>
          <Box sx={{ mb: 3 }}>
            <Typography
              component="h3"
              variant="h3"
              sx={{ my: 2 }}
            >{t`Filter by time range`}</Typography>
            <Box sx={{ display: 'flex', mb: 6 }}>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="created_after"
                value={currentFilters.created_after ? new Date(currentFilters.created_after) : null}
                dateToCompare={
                  currentFilters.created_before ? new Date(currentFilters.created_before) : null
                }
                dateToCompareFieldName="created_before"
                isStartDate={true}
                label={t`Start date`}
              />
              <Box sx={{ p: 2 }}>{` - `}</Box>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="created_before"
                value={
                  currentFilters.created_before ? new Date(currentFilters.created_before) : null
                }
                dateToCompare={
                  currentFilters.created_after ? new Date(currentFilters.created_after) : null
                }
                dateToCompareFieldName="created_after"
                isStartDate={false}
                label={t`End date`}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              component="h3"
              variant="h3"
              sx={{ my: 2 }}
            >{t`Filter by fields`}</Typography>
            <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
              <InputLabel>{t`Operator`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={currentFilters.operator_id}
                label={t`Operator`}
                autoWidth
                onChange={e => handleSelectChange(e, 'operator_id')}
              >
                {(filterChoices?.operators ?? []).map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
              <InputLabel>{t`Sales channel`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={currentFilters.sales_channel_name}
                label={t`Sales channel`}
                autoWidth
                onChange={e => handleSelectChange(e, 'sales_channel_name')}
              >
                {(filterChoices?.sales_channel_name ?? []).map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
              <InputLabel>{t`Payment method`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={currentFilters.payment_method}
                label={t`Payment method`}
                autoWidth
                onChange={e => handleSelectChange(e, 'payment_method')}
              >
                {(filterChoices?.payment_method ?? []).map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
              <InputLabel>{t`Refund status`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={currentFilters.refund_status}
                label={t`Refund status`}
                autoWidth
                onChange={e => handleSelectChange(e, 'refund_status')}
              >
                {(filterChoices?.refund_status ?? []).map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 3 }}>
              <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
              <Button onClick={clearFilters}>{t`Clear filters`}</Button>
            </Box>
          </Box>
        </AccordionDetails>
      )}
    </StyledAccordion>
  )
}

export { AffiliateOrderFilters }
